import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Download, FileText, FileType2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { UploadZone } from "@/components/app/UploadZone";
import { DeleteDocumentDialog } from "@/components/app/DeleteDocumentDialog";
import { DocumentReadingDialog } from "@/components/app/DocumentReadingDialog";
import { PhotoPdfDialog } from "@/components/app/PhotoPdfDialog";
import { PropertyFilter, PropertyTag } from "@/components/app/PropertySelect";
import { api, errorMessage } from "@/lib/api";
import { formatBytes, formatDate } from "@/lib/format";

async function download(wid, doc) {
  try {
    const res = await api.get(`/workspaces/${wid}/documents/${doc.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.original_filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error(e?.response?.status === 404 ? "Document not found." : errorMessage(e));
  }
}

const DocRow = ({ doc, wid, onDelete, onRead, properties }) => (
  <li data-testid={`document-row-${doc.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-navy/5">
        {doc.content_type === "application/pdf" ? <FileType2 className="h-5 w-5 text-navy" /> : <FileText className="h-5 w-5 text-navy" />}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-navy" data-testid="document-name">{doc.original_filename}</p>
        <p className="text-xs text-slate-500">
          {doc.content_type === "application/pdf" ? `PDF · ${doc.page_count} page${doc.page_count === 1 ? "" : "s"}` : "Text"} · {formatBytes(doc.size)} · Added {formatDate(doc.created_at)}
          {doc.is_sample && <span className="ml-2 rounded bg-gold-light px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gold-dark">Sample</span>}
        </p>
        <PropertyTag properties={properties} id={doc.property_id} />
        {doc.needs_ocr && <p className="mt-1 text-[11px] text-slate-500" data-testid="document-ocr-badge"><span className="rounded bg-navy/5 px-1.5 py-0.5 font-semibold text-navy">{doc.source === "photos" ? "Photo PDF" : "Scanned PDF"}</span> Text is recognized the first time you use Read with AI.</p>}
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" onClick={() => onRead(doc)} disabled={doc.is_sample} title={doc.is_sample ? "AI reading is turned off in the sample workspace" : undefined} data-testid="document-ai-read-button"><Sparkles className="mr-1.5 h-4 w-4 text-gold-dark" /> Read with AI</Button>
      <Button variant="outline" size="sm" onClick={() => download(wid, doc)} data-testid="document-download-button"><Download className="mr-1.5 h-4 w-4" /> Download</Button>
      <Button variant="outline" size="sm" onClick={() => onDelete(doc)} data-testid="document-delete-button" className="text-red-700 hover:bg-red-50 hover:text-red-800"><Trash2 className="h-4 w-4" /></Button>
    </div>
  </li>
);

export default function Documents() {
  const { workspace, properties } = useOutletContext();
  const [filter, setFilter] = useState(null);
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [toRead, setToRead] = useState(null);

  const load = useCallback(() => {
    api.get(`/workspaces/${workspace.id}/documents`, { params: { property_id: filter || undefined } }).then(({ data }) => setDocs(data.documents)).catch((e) => setError(errorMessage(e)));
  }, [workspace.id, filter]);
  useEffect(load, [load]);

  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow="Paperwork" title="Documents" subtitle="Your files are private to this workspace. Only signed-in members can open them." />
      <PropertyFilter properties={properties} value={filter} onChange={setFilter} />
      <UploadZone workspace={workspace} properties={properties} onUploaded={load} />
      <div className="mt-4 flex justify-end"><PhotoPdfDialog workspace={workspace} properties={properties} onCreated={load} /></div>
      <Panel title={`Saved documents${docs ? ` (${docs.length})` : ""}`} className="mt-8" testId="documents-list">
        {error && <p className="px-5 py-4 text-sm text-red-700">{error}</p>}
        {docs && docs.length === 0 && <Empty testId="documents-empty">No documents yet. Upload your first file above.</Empty>}
        {docs && docs.length > 0 && (
          <ul className="divide-y">{docs.map((d) => <DocRow key={d.id} doc={d} wid={workspace.id} onDelete={setToDelete} onRead={setToRead} properties={properties} />)}</ul>
        )}
      </Panel>
      <p className="mt-4 text-xs text-slate-500" data-testid="documents-ai-notice">{workspace.is_sample ? "AI reading is turned off in the sample workspace." : "Read with AI explains your PDFs and text files in plain language. For photos and scans, the text is recognized first, which uses one extra AI reading. It can be wrong, so check every detail against the document."}</p>
      <DocumentReadingDialog workspace={workspace} doc={toRead} onClose={() => setToRead(null)} />
      <DeleteDocumentDialog workspaceId={workspace.id} doc={toDelete} onClose={() => setToDelete(null)} onDeleted={load} />
    </div>
  );
}
